import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { editorViewCtx } from '@milkdown/kit/core';
import { useInstance } from '@milkdown/react';

import { reviewSuggestionsDecoKey } from './plugin';
import { orderReviewSuggestionsByResolvedRanges } from './selection';

const EMPTY_RESOLVED_IDS: ReadonlySet<string> = new Set();

/** Bounded RAF retries while waiting for the editor view + resolved ranges. ~10s at 60fps. */
const DEFAULT_SELECTION_RAF_RETRY_CAP = 600;

/**
 * Picks the default review-suggestion selection once the editor view exists AND the
 * suggestion ranges have resolved against the document — not from the async loader's raw
 * data.
 *
 * Selecting straight from the loader (before `editorViewCtx` is registered) leaves the rail
 * unable to read plugin state, so the card renders late. By reading plugin state here we
 * also select the FIRST suggestion in document order (the order produced *after* text
 * matching, via {@link orderReviewSuggestionsByResolvedRanges}) — matching the order the
 * rail renders — instead of the loader array's order.
 *
 * Ranges resolve synchronously on load standalone, but across Yjs sync transactions in
 * collab; neither re-renders React, so we poll via RAF (bounded) until they resolve or the
 * budget is exhausted. Only an empty/stale selection is filled — a still-valid current
 * selection is preserved (e.g. across a readOnly toggle / editor re-create).
 */
export function useDefaultReviewSuggestionSelection(
  hasReviewSuggestions: boolean,
  setSelectedReviewSuggestionId: Dispatch<SetStateAction<string | null>>,
): void {
  const [loadingInstance, getInstance] = useInstance();
  const appliedRef = useRef(false);

  useEffect(() => {
    if (loadingInstance || !hasReviewSuggestions) {
      // Editor (re)creating, or no suggestions yet: reset so the next ready editor re-applies.
      appliedRef.current = false;

      return undefined;
    }
    if (appliedRef.current) return undefined;

    let cancelled = false;
    let rafId: number | null = null;
    let attempts = 0;

    const tryApply = () => {
      if (cancelled) return;
      const editor = getInstance();
      if (!editor) return;

      let orderedIds: string[] | undefined;
      try {
        editor.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          const state = reviewSuggestionsDecoKey.getState(view.state);
          if (!state) return;
          orderedIds = orderReviewSuggestionsByResolvedRanges(state.suggestions, EMPTY_RESOLVED_IDS, state.ranges).map(
            (suggestion) => suggestion.id,
          );
        });
      } catch (error) {
        // The async loader gates view creation on a network fetch, so `editorViewCtx` can be
        // briefly unregistered; swallow that and retry next frame (matches the other hooks).
        if (!(error instanceof Error && error.message.includes('not found'))) throw error;
      }

      const firstId = orderedIds?.[0];
      if (!firstId) {
        // No resolvable suggestion yet (view not ready, or collab text not synced). Retry until
        // ranges resolve or we exhaust the budget, then stop probing.
        attempts += 1;
        if (attempts >= DEFAULT_SELECTION_RAF_RETRY_CAP) {
          appliedRef.current = true;

          return;
        }
        rafId = requestAnimationFrame(tryApply);

        return;
      }

      appliedRef.current = true;
      const resolvableIds = orderedIds ?? [];
      setSelectedReviewSuggestionId((currentSelectedId) =>
        currentSelectedId && resolvableIds.includes(currentSelectedId) ? currentSelectedId : firstId,
      );
    };

    tryApply();

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [loadingInstance, hasReviewSuggestions, getInstance, setSelectedReviewSuggestionId]);
}
